import { useEffect } from "react";
import {
  Controller,
  FormProvider,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button, Switch, Text } from "@pentabd/ui";

import {
  HeaderWithNextButton,
  InputDateControllerRow,
  InputTextRow,
  MultiFileRow,
  NextButton,
} from "@components";
import { TenderNoticePaper } from "./components";

import { getAccess } from "@helpers/permissionController";
import { useTenderNoticePublication } from "@hooks/procurements/procurement/createProcurements/useTenderNoticePublicaiton";
import { useUser } from "@hooks/useUser";

import {
  TNPDetailResponseType,
  TenderNoticePublicationType,
} from "@type/procurements/createProcurement/tenderNoticePublication";
import {
  convertMultipleDocumentData,
  revertMultipleDocumentData,
} from "@utils/documentDataConvert";

import { FORM_FIELDS } from "@constants/form";

export const TENDER_NOTICE = FORM_FIELDS.PROCUREMENT_DETAILS.TENDER_NOTICE;

export interface TenderNoticePropType {
  id: any;
  changeActiveTab: (arg: number) => void;
  setIsSuccessModalOpen: (arg: boolean) => void;
  setIsErrorModalOpen: (arg: boolean) => void;
}

const TenderNotice = ({
  id,
  changeActiveTab,
  setIsSuccessModalOpen,
  setIsErrorModalOpen,
}: TenderNoticePropType) => {
  const { t } = useTranslation();

  const { user } = useUser();
  const { realm_roles: roles } = user || {};

  const { createTenderNoticePublication, TNPData } =
    useTenderNoticePublication(id);

  const { getSingleData, createError, createSuccess } =
    TNPData || ({} as TNPDetailResponseType);

  const { data } = getSingleData || ({} as TenderNoticePublicationType);

  const method = useForm({
    values: revertMultipleDocumentData(
      data,
      [TENDER_NOTICE.CORRIGENDUM_DOCUMENT, TENDER_NOTICE.MEETING_DOCUMENT],
      [{ fieldName: "papersInfo", docName: TENDER_NOTICE.NOTICE_DOCUMENT }],
    ) as TenderNoticePublicationType,
  });

  const { control, register, setValue, handleSubmit } = method;

  const formDataFormatter = (data: any) => {
    const formattedPapersInfo = data?.papersInfo?.map((item: any) => {
      const formattedNoticeDocument = item?.noticeDocuments?.map(
        (nestedItem: any) => {
          return {
            ...(nestedItem || {}),
            remarks: data[`remarks-${nestedItem?.documentId}`] || "",
            documentName: data[`documentName-${nestedItem?.documentId}`] || "",
            memorialNo: data[`memorialNo-${nestedItem?.documentId}`] || "",
            date: data[`date-${nestedItem?.documentId}`] || null,
          };
        },
      );

      item.noticeDocuments = formattedNoticeDocument || [];

      return item;
    });

    data.papersInfo = formattedPapersInfo || [];

    return data;
  };

  const onSubmit: SubmitHandler<any> = (data: TenderNoticePublicationType) => {
    const __data = {
      ...convertMultipleDocumentData(data, [
        TENDER_NOTICE.CORRIGENDUM_DOCUMENT,
        TENDER_NOTICE.MEETING_DOCUMENT,
      ]),
      ...formDataFormatter(data),
    };

    const reqData = { ...__data };
    reqData.deliveryPeriodDays = Number(reqData.deliveryPeriodDays);
    createTenderNoticePublication(reqData, id, data?.id);
  };

  useEffect(() => {
    switch (true) {
      case createSuccess: {
        // fetchTNPDetails(id);
        setIsSuccessModalOpen(true);
        break;
      }
      case !!createError: {
        setIsErrorModalOpen(true);
        break;
      }
      default: {
        setIsSuccessModalOpen(false);
        break;
      }
    }
  }, [createSuccess, createError]);

  return (
    <div className="p-10">
      <HeaderWithNextButton
        title="PROCUREMENT_CREATE.TENDER_NOTICE_PUBLICATION.HEADER.TITLE"
        action={() => changeActiveTab(3)}
      />

      <FormProvider {...method}>
        <form
          className="container py-10 border-bottom d-flex flex-column gap-6"
          onSubmit={handleSubmit(onSubmit)}
        >
          <TenderNoticePaper />

          <div className="d-flex flex-column gap-6 py-10">
            <div className="row">
              <div className="col-md-12 col-lg-3">
                <Text weight="semibold" color="title" size="sm" component="p">
                  {t(
                    "PROCUREMENT_CREATE.TENDER_NOTICE_PUBLICATION.FORM.TENDER_PUBLISHED_WEBSITE.LABEL",
                  )}
                </Text>
              </div>
              <div className="col-md-12 col-lg-7">
                <Controller
                  control={control}
                  name={TENDER_NOTICE.PUBLISH_WEBSITE}
                  render={({ field }) => {
                    return (
                      <Switch
                        {...register(TENDER_NOTICE.PUBLISH_WEBSITE)}
                        id={TENDER_NOTICE.PUBLISH_WEBSITE}
                        name="groups"
                        size="sm"
                        value={field.value}
                        onChange={(e) =>
                          setValue(
                            TENDER_NOTICE.PUBLISH_WEBSITE,
                            e.target.checked,
                          )
                        }
                        controlling
                      />
                    );
                  }}
                />
              </div>
            </div>

            <div className="row py-6">
              <div className="col-md-12 col-lg-3">
                <Text weight="semibold" color="title" size="sm" component="p">
                  {t(
                    "PROCUREMENT_CREATE.TENDER_NOTICE_PUBLICATION.FORM.TENDER_MEETING.LABEL",
                  )}
                </Text>
              </div>
              <div className="col-md-12 col-lg-7">
                <Controller
                  control={control}
                  name={TENDER_NOTICE.MEETING}
                  render={({ field }) => {
                    return (
                      <Switch
                        {...register(TENDER_NOTICE.MEETING)}
                        id={TENDER_NOTICE.MEETING}
                        name="groups"
                        size="sm"
                        value={field.value}
                        onChange={(e) =>
                          setValue(TENDER_NOTICE.MEETING, e.target.checked)
                        }
                        controlling
                      />
                    );
                  }}
                />
              </div>
            </div>

            <MultiFileRow
              label="PROCUREMENT_CREATE.TENDER_NOTICE_PUBLICATION.FORM.TENDER_MEETING_DOC"
              name={TENDER_NOTICE.MEETING_DOCUMENT}
              control={control}
              setValue={setValue}
              register={register}
              fileDetails={["documentName", "date", "remarks", "memorialNo"]}
            />

            <InputDateControllerRow
              control={control}
              label="PROCUREMENT_CREATE.TENDER_NOTICE_PUBLICATION.FORM.TENDER_PUBLICATION_DATE"
              register={{ ...register(TENDER_NOTICE.PAPER_DATE) }}
              name={TENDER_NOTICE.PUBLICATION_DATE}
            />

            <InputDateControllerRow
              control={control}
              label="PROCUREMENT_CREATE.TENDER_NOTICE_PUBLICATION.FORM.TENDER_SUBMISSION_DATE"
              register={{ ...register(TENDER_NOTICE.PAPER_DATE) }}
              name={TENDER_NOTICE.SUBMISSION_DATE}
            />

            <InputDateControllerRow
              control={control}
              label="PROCUREMENT_CREATE.TENDER_NOTICE_PUBLICATION.FORM.TENDER_OPENING_DATE"
              register={{ ...register(TENDER_NOTICE.PAPER_DATE) }}
              name={TENDER_NOTICE.OPENING_DATE}
            />

            <InputTextRow
              label="PROCUREMENT_CREATE.TENDER_NOTICE_PUBLICATION.FORM.TENDER_DELIVERY_PERIOD"
              register={{ ...register(TENDER_NOTICE.DELIVERY_PERIOD) }}
            />

            <MultiFileRow
              name={TENDER_NOTICE.CORRIGENDUM_DOCUMENT}
              control={control}
              setValue={setValue}
              register={register}
              fileDetails={["documentName", "date", "remarks", "memorialNo"]}
            />
          </div>

          <div className="row">
            <div className="col-lg-3"></div>

            <div className="d-flex justify-content-end gap-8 col-lg-7">
              {getAccess(roles, "PROCUREMENT_HR") && (
                <Button
                  fill="outline"
                  type="primary"
                  htmlType="submit"
                  size="sm"
                >
                  {t(
                    "PROCUREMENT_CREATE.TENDER_NOTICE_PUBLICATION.FORM.SAVE_BUTTON_TEXT",
                  )}
                </Button>
              )}
            </div>
          </div>
        </form>
        <div className="text-end mt-10">
          <NextButton action={() => changeActiveTab(3)} />
        </div>
      </FormProvider>
    </div>
  );
};

export default TenderNotice;
